import { ref, watch, onMounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import i18n from '@/libs/i18n'

/** หมวดของ poi — ต้องตรงกับ helpers/poi-classify.helper.js ฝั่ง API */
export const POI_CATEGORIES = [
  { value: 'school', text: 'โรงเรียน' },
  { value: 'international_school', text: 'โรงเรียนนานาชาติ' },
  { value: 'university', text: 'มหาวิทยาลัย / วิทยาลัย' },
  { value: 'department_store', text: 'ห้างสรรพสินค้า / ศูนย์การค้า' },
  { value: 'supermarket', text: 'ซูเปอร์มาร์เก็ต' },
  { value: 'convenience_store', text: 'ร้านสะดวกซื้อ' },
  { value: 'hospital', text: 'โรงพยาบาล' },
  { value: 'clinic', text: 'คลินิก' },
  { value: 'veterinary', text: 'โรงพยาบาลสัตว์' },
]

export default function usePoiList() {
  const toast = useToast()
  const refListTable = ref(null)
  const items = ref([])
  const totals = ref(0)
  const isLoading = ref(false)
  const perPage = ref(25)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const filters = ref({ category: null, source: null, is_active: null })

  const tableColumns = [
    { key: 'id', label: '#' },
    { key: 'name', label: 'ชื่อ' },
    { key: 'category', label: 'หมวด' },
    { key: 'coord', label: 'พิกัด' },
    { key: 'source', label: 'ที่มา' },
    { key: 'is_active', label: 'สถานะ' },
    { key: 'actions', label: '' },
  ]

  const categoryOptions = [{ value: null, text: 'ทุกหมวด' }, ...POI_CATEGORIES]

  const sourceOptions = [
    { value: null, text: 'ทั้งหมด' },
    { value: 'osm', text: 'OpenStreetMap' },
    { value: 'manual', text: 'เพิ่มเอง' },
  ]

  const activeOptions = [
    { value: null, text: 'ทั้งหมด' },
    { value: 1, text: 'ใช้งาน' },
    { value: 0, text: 'ปิด' },
  ]

  const categoryLabel = POI_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.value]: c.text }), {})

  /** ชื่อตามภาษาที่ใช้อยู่ ไม่มีก็ถอยไปอังกฤษ แล้วค่อยภาษาใดก็ได้ที่มี */
  const displayName = item => {
    const name = item && item.name ? item.name : {}
    const locale = i18n && i18n.locale ? i18n.locale : 'en'

    return name[locale] || name.en || Object.values(name).find(Boolean) || '(ไม่มีชื่อ)'
  }

  const fetchData = () => {
    isLoading.value = true

    store
      .dispatch('app-poi/fetchData', {
        perpage: perPage.value,
        offset: (currentPage.value - 1) * perPage.value,
        keyword: searchQuery.value || null,
        category: filters.value.category,
        source: filters.value.source,
        is_active: filters.value.is_active,
      })
      .then(response => {
        items.value = response.data.data || []
        totals.value = response.data.total || 0
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: { title: 'โหลดรายการไม่สำเร็จ', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      })
      .finally(() => { isLoading.value = false })
  }

  const deleteData = id => {
    store
      .dispatch('app-poi/fetchDelete', id)
      .then(() => {
        toast({
          component: ToastificationContent,
          props: { title: 'ลบแล้ว', icon: 'CheckIcon', variant: 'success' },
        })
        fetchData()
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: { title: 'ลบไม่สำเร็จ', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      })
  }

  /* เปลี่ยนตัวกรอง = เริ่มนับหน้าใหม่ ไม่งั้นค้างอยู่หน้าที่ไม่มีข้อมูลแล้ว */
  watch([searchQuery, filters], () => {
    if (currentPage.value !== 1) currentPage.value = 1
    else fetchData()
  }, { deep: true })

  watch([currentPage, perPage], fetchData)

  onMounted(fetchData)

  return {
    refListTable,
    items,
    totals,
    isLoading,
    perPage,
    currentPage,
    searchQuery,
    filters,
    tableColumns,
    categoryOptions,
    sourceOptions,
    activeOptions,
    categoryLabel,
    displayName,
    fetchData,
    deleteData,
  }
}
